import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaMicrophone, FaTimes } from "react-icons/fa";
/* Reuses the permission-flow screenshot: it already shows the browser dropdown
   with the Microphone toggle, which is exactly what the first tip describes. */
import MicPermissionImage from "assets/images/permission-tab-image-2.png";
import IncognitoImage from "assets/images/incognito.png";

// Screenshots keyed by tip index. Tips without an entry render as text only.
const TIP_IMAGES = {
  0: MicPermissionImage,
  4: IncognitoImage,
};

// Indices whose screenshot is tall and portrait. On mobile these fill the card
// width and crop from the bottom rather than shrinking to an unreadable
// thumbnail; the Microphone row sits in the upper half so it stays visible.
const TALL_TIP_IMAGES = [0];

/**
 * Helper modal listing what a candidate can check when their mic records no
 * audio. Deliberately advice-only: the recorder itself sits on the screen
 * behind this modal, so repeating a live tester here would duplicate it.
 *
 * Dismissible — no-audio is recoverable and the candidate needs to get back to
 * the recorder.
 */
export const AudioTroubleshootModal = ({ visible = false, onClose }) => {
  const { t } = useTranslation();
  const closeRef = useRef(null);
  // Indices of tips whose screenshot the candidate has opened. Only affects
  // mobile — the `sm:` rules below show every screenshot inline on desktop.
  const [expanded, setExpanded] = useState([]);

  const toggleImage = (index) => {
    setExpanded((current) =>
      current.includes(index)
        ? current.filter((i) => i !== index)
        : [...current, index]
    );
  };

  // Move focus into the dialog on open so keyboard and screen-reader users land
  // on it, and close on Escape.
  useEffect(() => {
    if (!visible) return;

    closeRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [visible, onClose]);

  if (!visible) return <></>;

  const tips = t("audio.troubleshoot.tips", { returnObjects: true });
  const tipList = Array.isArray(tips) ? tips : [];

  /* Rendered into document.body: this modal mounts deep inside the recorder,
     and any ancestor with transform/filter/backdrop-filter would make `fixed`
     resolve against that element instead of the viewport — clipping the modal
     inside the quiz card. */
  return createPortal(
    <div
      className="fixed inset-0 z-[50] flex items-center justify-center bg-black/[0.35] px-2 py-4 sm:px-4 sm:py-6"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="audio-troubleshoot-title"
        /* Capped at 900px: past that the two text columns get wider than a
           comfortable reading measure.

           max-h + overflow is a safety net, not the normal case: collapsed,
           the content fits without scrolling. It only engages when a candidate
           expands both screenshots, which would otherwise clip the last card
           and the Got it button off-screen with no way to reach them. */
        className="relative flex max-h-[92vh] w-full max-w-[340px] flex-col overflow-y-auto rounded-xl bg-white p-3 text-left shadow-lg sm:max-h-[92vh] sm:max-w-[min(80vw,900px)] sm:rounded-2xl sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={closeRef}
          type="button"
          aria-label={t("audio.troubleshoot.close")}
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
          onClick={onClose}
        >
          <FaTimes aria-hidden="true" />
        </button>

        <div className="flex items-center gap-2 border-b border-stark-white/50 pb-2.5 pr-7 sm:gap-3.5 sm:pb-4 sm:pr-8">
          <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full border border-stark-white bg-seashell text-[11px] text-cocoa-brown sm:h-9 sm:w-9 sm:text-base">
            <FaMicrophone aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h2
              id="audio-troubleshoot-title"
              className="text-[13px] font-medium leading-[18px] text-cocoa-brown sm:text-lg sm:leading-6"
            >
              {t("audio.troubleshoot.title")}
            </h2>
          </div>
        </div>

        <div className="mt-2.5 sm:mt-4">
          <p className="mb-1.5 text-[9px] font-medium uppercase tracking-[0.08em] text-cocoa-brown/45 sm:mb-2.5 sm:text-xs">
            {t("audio.troubleshoot.tips_heading")}
          </p>
          {/* Masonry columns rather than a fixed grid: each tip is a
              self-contained card and the two carrying screenshots are far
              taller than the rest, so letting a short card sit directly under a
              tall one avoids the row gaps a grid would leave. Single column on
              mobile: with the screenshots collapsed the cards are short enough
              to stack, and full-width text is more readable. */}
          <ul className="sm:columns-2 sm:gap-4">
            {tipList.map((tip, index) => (
              <li
                key={index}
                /* break-inside keeps a card and its screenshot in one column. */
                className="mb-1.5 block break-inside-avoid rounded-lg bg-seashell/60 p-2.5 text-[11.5px] leading-[1.45] last:mb-0 sm:mb-3 sm:rounded-xl sm:p-3.5 sm:text-sm sm:leading-[1.55]"
              >
                <span className="block min-w-0">
                  <span className="font-semibold text-cocoa-brown">{tip.lead}</span>
                  {tip.rest ? (
                    <span className="text-cocoa-brown/70"> {tip.rest}</span>
                  ) : null}
                  {TIP_IMAGES[index] ? (
                    <>
                      {/* Mobile only: the screenshot stays collapsed so five
                          cards fit without scrolling. Desktop shows it inline,
                          so this control is hidden there. */}
                      <button
                        type="button"
                        aria-expanded={expanded.includes(index)}
                        onClick={() => toggleImage(index)}
                        /* flex, not inline-flex: the tip text is inline
                           content, so an inline toggle runs on from the last
                           word ("Allow.Show me how"). */
                        className="mt-2 flex items-center gap-1.5 text-xs font-medium text-cocoa-brown/75 sm:hidden"
                      >
                        {t("audio.troubleshoot.show_me_how")}
                        <FaChevronDown
                          aria-hidden="true"
                          className={`text-[9px] transition-transform duration-200 ${
                            expanded.includes(index) ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      <span
                        className={`mt-2 sm:mt-2.5 sm:block ${
                          expanded.includes(index) ? "block" : "hidden"
                        }`}
                      >
                        {/* Short/wide shots are contained so the whole frame
                            shows. Tall portrait ones fill the card width on
                            mobile and crop from the bottom instead, since
                            containing them yields an unreadable thumbnail. */}
                        <img
                          src={TIP_IMAGES[index]}
                          alt=""
                          className={
                            TALL_TIP_IMAGES.includes(index)
                              ? "block h-auto max-h-[185px] w-full rounded-md border border-stark-white object-cover object-top sm:max-h-[280px] sm:rounded-lg"
                              : "block h-auto max-h-[110px] w-auto max-w-full rounded-md border border-stark-white object-contain sm:max-h-[190px] sm:rounded-lg"
                          }
                        />
                      </span>
                    </>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-3 flex justify-end border-t border-stark-white/60 pt-3 sm:mt-4 sm:pt-4">
          <button
            className="w-full rounded-lg border border-stark-white px-6 py-2 text-xs font-medium text-cocoa-brown transition hover:bg-seashell sm:w-auto sm:py-2.5 sm:text-sm"
            onClick={onClose}
          >
            {t("audio.troubleshoot.dismiss")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
